import { useEffect, useRef } from 'react';

/**
 * Draft autosave for long forms.
 *
 * The problem (UX review #23): a builder fills in half an accident
 * report or a RAMS, their phone rings, they take the call, the page
 * tab gets killed by iOS Safari's aggressive memory reaper, and the
 * whole form is gone. The user reaches for a tape gun and never trusts
 * the app with anything serious again.
 *
 * The fix: persist the in-flight form state to localStorage on a 5s
 * debounced interval. On re-entering the page, surface a "Pick up
 * where you left off?" banner so the user can restore or start fresh.
 *
 * What we DO save:
 *   - Plain text fields, dropdown selections, checkbox state, witness
 *     lists, anything serialisable as JSON.
 * What we DON'T save:
 *   - Signature canvas state (binary, awkward, rarely worth restoring —
 *     the user can re-sign in 2 seconds).
 *   - Photo blobs (would blow localStorage quota fast; LocalPhoto in
 *     particular holds File objects that don't serialise).
 *   - Anything that's already on the server (other forms' state).
 *
 * Keys are namespaced by form kind + record id so multiple in-flight
 * forms don't collide. Drafts older than 7 days are auto-purged on
 * read — a half-finished accident report from last week probably
 * shouldn't re-appear in the middle of next week's job.
 */

const KEY_PREFIX = 'sas:draft:';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface StoredDraft<T> {
  payload: T;
  savedAt: number;
}

/** Read a saved draft, or null if none exists / it's expired. */
export function loadDraft<T = unknown>(key: string): StoredDraft<T> | null {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft<T>;
    if (!parsed || typeof parsed.savedAt !== 'number') {
      window.localStorage.removeItem(KEY_PREFIX + key);
      return null;
    }
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      window.localStorage.removeItem(KEY_PREFIX + key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Write the current form state under {key}. Throttling is the caller's job —
 *  inside `useDraftAutosave` we debounce; one-off calls (e.g. tab blur) just
 *  call this directly. Soft-fails on quota / private-mode / no-storage. */
export function saveDraft<T>(key: string, payload: T): void {
  try {
    const stored: StoredDraft<T> = { payload, savedAt: Date.now() };
    window.localStorage.setItem(KEY_PREFIX + key, JSON.stringify(stored));
  } catch {
    // localStorage may be unavailable in private-browsing or full quota.
    // Autosave is best-effort, not a guarantee — swallow.
  }
}

/** Remove a saved draft. Call after a successful form submit. */
export function clearDraft(key: string): void {
  try {
    window.localStorage.removeItem(KEY_PREFIX + key);
  } catch {
    /* noop */
  }
}

/**
 * Auto-save the supplied payload to localStorage on a debounced interval
 * (default 5 seconds after the last change). Also flushes on tab-blur and
 * before-unload so a quick phone-call-causes-tab-kill doesn't lose the last
 * 5 seconds of typing.
 *
 * The caller is responsible for:
 *   - Loading the draft on mount (via `loadDraft`) and offering a restore UX.
 *   - Calling `clearDraft(key)` once the form has submitted successfully.
 */
export function useDraftAutosave<T>(
  key: string,
  payload: T,
  opts: { intervalMs?: number; enabled?: boolean } = {},
): void {
  const intervalMs = opts.intervalMs ?? 5000;
  const enabled = opts.enabled !== false;
  const payloadRef = useRef<T>(payload);
  const dirtyRef = useRef(false);

  // Track the latest payload so the debounce flush always saves the freshest.
  useEffect(() => {
    payloadRef.current = payload;
    dirtyRef.current = true;
  }, [payload]);

  // Debounced interval flush.
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      if (!dirtyRef.current) return;
      saveDraft(key, payloadRef.current);
      dirtyRef.current = false;
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [key, intervalMs, enabled]);

  // Flush immediately when the tab loses focus or before unload — covers the
  // "iOS Safari killed the tab when the phone rang" path. We don't try to
  // make this perfect (iOS doesn't always fire these on backgrounding) but
  // catching even half of those losses is a clear win.
  useEffect(() => {
    if (!enabled) return;
    const flush = () => {
      if (dirtyRef.current) {
        saveDraft(key, payloadRef.current);
        dirtyRef.current = false;
      }
    };
    window.addEventListener('blur', flush);
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('blur', flush);
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', flush);
    };
  }, [key, enabled]);
}

/** Helper to format a "saved 2 minutes ago" timestamp for the restore banner. */
export function formatDraftAge(savedAt: number, now = Date.now()): string {
  const diffSec = Math.max(0, Math.round((now - savedAt) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}
